/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, getter-return, no-prototype-builtins */
import Controller from '@ember/controller';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';

export default class HouseYearLevelCountsController extends Controller {
  @service
  session;

  @service
  fieldOptions;

  title = 'Year Level Counts by House';

  @alias('session.school')
  school;

  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('fieldOptions.yearLevelOptions.[]')
  get yearLevelMap() {
    if (!isEmpty(this.fieldOptions.yearLevelOptions)) {
      return this.fieldOptions.yearLevelOptions[0].options;
    }
  }

  @computed('school.{yearLevels.[],schoolType}')
  get yearLevels() {
    let yearLevels = this.school.yearLevels;
    if (parseInt(this.school.schoolType, 10) === 21) {
      // We add years 7 & 8 for Contributing Primary Schools for this report
      yearLevels.push('7', '8');
    }
    return yearLevels;
  }

  @computed('model.fields.[]')
  get field() {
    return this.model.fields.find((f) => f.key === 'custom:house');
  }

  @computed(
    'field',
    'model.people.[]',
    'yearLevelMap.[]',
    'jurisdiction',
    'yearLevels.[]',
  )
  get tableData() {
    let houses = this.field.get('options');

    return houses.reduce((data, house) => {
      let houseMembers = this.model.learners.filter(
        (item) => item.fields['custom:house']?.value === house.value,
      );

      data[house.value] = houseMembers.reduce((acc, learner) => {
        let gender = learner.gender;
        let yearLevel = learner.currentYearLevel;

        if (acc.hasOwnProperty(yearLevel)) {
          if (acc[yearLevel].hasOwnProperty(gender)) {
            acc[yearLevel][gender]++;
          } else {
            acc[yearLevel][gender] = 1;
          }
        } else {
          acc[yearLevel] = {
            [gender]: 1,
          };
        }

        return acc;
      }, {});

      return data;
    }, {});
  }

  @computed('tableData')
  get yearGenderTotals() {
    return Object.values(this.tableData).reduce((acc, years) => {
      Object.entries(years).forEach((entry) => {
        let year = entry[0];
        let genderCounts = entry[1];

        if (!acc.hasOwnProperty(year)) {
          acc[year] = {};
        }

        Object.entries(genderCounts).forEach((entry) => {
          let gender = entry[0];
          let genderCount = entry[1];

          if (acc[year].hasOwnProperty(gender)) {
            acc[year][gender] += genderCount;
          } else {
            acc[year][gender] = genderCount;
          }
        });
      });

      return acc;
    }, {});
  }

  @computed('yearGenderTotals')
  get yearTotals() {
    return Object.entries(this.yearGenderTotals).reduce((years, entry) => {
      let year = entry[0];
      let genders = entry[1];

      years[year] = Object.values(genders).reduce(
        (acc, count) => acc + count,
        0,
      );

      return years;
    }, {});
  }

  @computed('yearLevels.[]', 'tableData')
  get houseGenderTotals() {
    return Object.keys(this.tableData).reduce((data, key) => {
      let house = this.tableData[key];

      data[key] = this.yearLevels.reduce((totals, year) => {
        let genders = house[year];
        if (isEmpty(genders)) {
          return totals;
        }

        Object.keys(genders).forEach((gender) => {
          if (totals.hasOwnProperty(gender)) {
            totals[gender] += genders[gender];
          } else {
            totals[gender] = genders[gender];
          }
        });

        return totals;
      }, {});

      return data;
    }, {});
  }

  @computed('houseGenderTotals')
  get houseTotals() {
    return Object.entries(this.houseGenderTotals).reduce((rooms, entry) => {
      let room = entry[0];
      let genders = entry[1];

      rooms[room] = Object.values(genders).reduce(
        (acc, count) => acc + count,
        0,
      );

      return rooms;
    }, {});
  }

  @computed('houseGenderTotals')
  get totals() {
    let genders = Object.values(this.houseGenderTotals).reduce(
      (genders, room) => {
        Object.entries(room).forEach((entry) => {
          let gender = entry[0];
          let count = entry[1];

          if (genders.hasOwnProperty(gender)) {
            genders[gender] += count;
          } else {
            genders[gender] = count;
          }
        });

        return genders;
      },
      {},
    );

    return {
      genders,
      total: Object.values(genders).reduce((acc, val) => acc + val, 0),
    };
  }
}
